import { View, normalizeDate } from '../dateUtils';
import { resolveProductIdentifier } from '../platformUtils';
import { EmailProductData } from '../../stores/appOrderHistoryStore';

/**
 * Shared helpers for the Reports page (frontend ports of the server-side data
 * export math). Pure, React-free, unit-testable. All report computation modules
 * in this folder build on these primitives.
 */

/** A resolved report period. `comparisonHidden` is true for the "all" view, where
 * no equal-length previous period exists (mirrors the data export / weekly email). */
export interface ReportRange {
  start: Date;
  end: Date;
  comparisonHidden: boolean;
}

/**
 * Product identity key for report AGGREGATION, mirroring `dataExportService.ts`
 * `productKey`: `${platform}::${resolveProductIdentifier(...)}`. The resolver is the
 * single source of truth for "which product is this line?" — it splits reused
 * "restgroep" article ids (vhcjongens 45.100.13 → fingerlimes/watermeloen/morilles),
 * folds sentinel ids (`no_article_id`, …) and `platformsWithoutArticleId` platforms
 * onto the normalized name, else keys by the raw article id. The platform prefix keeps
 * the same article id on two suppliers apart.
 *
 * This is DELIBERATELY not the inventory key: inventory matching keeps its own raw rule
 * (`inventoryKey` in consumptionReport.ts — raw name, no split), so anything
 * inventory-facing must NOT be derived from this string.
 */
/** A product-key builder: aggregation (`productKey`) or inventory (`inventoryKey`). */
export type ProductKeyFn = (platform: string, articleId: string | undefined, name: string | undefined) => string;

export function productKey(
  platform: string,
  articleId: string | undefined,
  name: string | undefined
): string {
  const p = (platform || '').toLowerCase();
  const identifier = resolveProductIdentifier(platform, articleId, name) ?? '';
  return `${p}::${identifier}`;
}

/** Inclusive number of days between two dates (both ends counted). */
export function inclusiveDays(start: Date, end: Date): number {
  const ms = normalizeDate(end).getTime() - normalizeDate(start).getTime();
  return Math.round(ms / 86_400_000) + 1;
}

/**
 * Resolve the shared time-controls store (`view` + `startDate`/`endDate`, the same
 * model the dashboard / order-history `TopSection` drives) into a report range.
 * For day/week/month/quarter the range is the selected period; for "all" the range
 * spans the full data window and the previous-period comparison is hidden — exactly
 * matching how the dashboard treats "all time".
 */
export function rangeFromTimeControls(view: View, startDate: Date, endDate: Date): ReportRange {
  return {
    start: normalizeDate(startDate),
    end: normalizeDate(endDate),
    comparisonHidden: view === 'all',
  };
}

/**
 * The equal-length period immediately before `range` (mirrors `previousPeriod()`
 * in `dataExportService.ts:102`). Returns null when comparison is hidden ("all").
 */
export function previousRange(range: ReportRange): ReportRange | null {
  if (range.comparisonHidden) return null;
  const days = inclusiveDays(range.start, range.end);
  const prevEnd = new Date(range.start);
  prevEnd.setDate(prevEnd.getDate() - 1);
  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - (days - 1));
  return { start: normalizeDate(prevStart), end: normalizeDate(prevEnd), comparisonHidden: false };
}

/** Price per std unit (€/g, €/ml, €/pc) for an order product, ex-VAT. Returns null
 *  when the product has no usable `quantityInfo`. Mirrors server `pricePerStdUnit`. */
export function pricePerStdUnit(product: EmailProductData): number | null {
  const stdAmount = product.quantityInfo?.stdAmount;
  const stdMultiplier = product.quantityInfo?.stdMultiplier;
  if (!stdAmount || !stdMultiplier) return null;
  return Math.abs(product.price / (stdAmount * stdMultiplier));
}

/** Whether `date` falls within `range`, inclusive of both day-boundaries. */
export function inRange(date: Date, range: { start: Date; end: Date }): boolean {
  const d = normalizeDate(date).getTime();
  return d >= normalizeDate(range.start).getTime() && d <= normalizeDate(range.end).getTime();
}

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/**
 * Format a base-unit quantity for display, mirroring `dataExportService.ts`
 * `fmtBase`: grams → kg, millilitres → L, pieces → pcs, otherwise rounded.
 */
export function fmtBase(value: number, stdUnit: string): string {
  if (stdUnit === 'g') return `${(value / 1000).toFixed(2)} kg`;
  if (stdUnit === 'ml') return `${(value / 1000).toFixed(2)} L`;
  if (stdUnit === 'p') return `${Math.round(value)} pcs`;
  return String(round3(value));
}

/**
 * Like `fmtBase` but keeps fractional piece counts (2 decimals) instead of
 * rounding — for per-unit figures like "0.2 pcs per glass", where `fmtBase`
 * would misleadingly show "0 pcs".
 */
export function fmtBasePrecise(value: number, stdUnit: string): string {
  if (stdUnit === 'p') return `${Math.round(value * 100) / 100} pcs`;
  return fmtBase(value, stdUnit);
}

/**
 * A quantity in the unit staff actually count in ("12 fles", "3.5 doos").
 *
 * NOT `fmtBase`: that one is hard-wired to the BASE units (g→kg, ml→L, p→pcs) and would
 * rescale a count of bottles by 1000 or round it to whole pieces. The count unit is free
 * text the customer typed, so it prints verbatim. Two decimals with trailing zeros
 * trimmed — a fractional bottle is a real quantity, and rounding it to 0 hides stock.
 */
export function fmtCountUnit(value: number, countUnit: string): string {
  return `${Math.round(value * 100) / 100} ${countUnit}`;
}

/**
 * `fmtBase` for a quantity that may not exist at all — a product whose two counts were
 * frozen in unit spaces nothing can bridge has no quantity in ANY space, and the reports
 * send null for it rather than a 0. Use this instead of `fmtBase(qty ?? 0, …)`: rendering
 * that null as "0 kg" is how phantom stock reaches a screen, and it has shipped twice.
 */
export function fmtBaseOrDash(value: number | null | undefined, stdUnit: string): string {
  return value === null || value === undefined ? '—' : fmtBase(value, stdUnit);
}
